import { useState } from 'react'
import useSWR from 'swr'
import * as Sentry from '@sentry/nextjs'

import { useAuthContext } from 'components/Auth/AuthContextProvider'

import serializeParameters from './serializeParameters'

const useIWitnessAccessToken = (
  enabled = true
): IWitnessAccessToken | undefined => {
  const [refreshInterval, setRefreshInterval] = useState(3600 * 1000)

  const { isSignedIn } = useAuthContext()

  // if SSO is disabled, then we can't get an API access token
  if (process.env.NEXT_PUBLIC_USE_SSO !== 'true') {
    enabled = false
  }

  const baseUrl = process.env.NEXT_PUBLIC_SSO_AUTHORITY ?? ''
  const url = `${baseUrl}/connect/token`

  const body = serializeParameters({
    client_id: process.env.NEXT_PUBLIC_IWALK_API_CLIENT_ID ?? '',
    client_secret: process.env.NEXT_PUBLIC_IWALK_API_CLIENT_SECRET ?? '',
    grant_type: 'client_credentials',
  })

  const { data } = useSWR<IWitnessAccessToken>(
    enabled && isSignedIn ? url : null,
    async (url: string) => {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Cache-Control': 'no-cache',
        },
        body,
      })

      if (!response.ok) {
        const error = new Error(
          `An error occurred while fetching access token: ${response.status} for ${url}`
        )

        Sentry.captureException(error)

        throw error
      }

      return response.json()
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      refreshInterval,
      onSuccess: data => setRefreshInterval(data.expires_in * 1000),
    }
  )

  return data
}

export default useIWitnessAccessToken
